import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { NbDialogRef, NbDialogService, NbToastrService, NbCardModule, NbRadioModule, NbButtonModule, NbInputModule } from '@nebular/theme';
import { BehaviorSubject, Subscription, of } from 'rxjs';
import { KPI } from '../../../@core/api';
import { EmissionFactorService } from '../../../@core/utils/emission-factor.service';
import { switchMap } from 'rxjs/operators';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { EmissionFactorDialogComponent } from '../emission-factor-dialog/emission-factor-dialog.component';
import { ErrorService, PerimetersService, UsersService } from '../../../@core/utils';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { FormsModule } from '@angular/forms';

@Component({
    selector: 'app-emission-factor-list-dialog',
    templateUrl: './emission-factor-list-dialog.component.html',
    styleUrls: ['./emission-factor-list-dialog.component.scss'],
    standalone: true,
    imports: [NbCardModule, NbRadioModule, FormsModule, NbButtonModule, NbInputModule, FontAwesomeModule, TranslateModule]
})
export class EmissionFactorListDialogComponent implements OnInit, OnDestroy {

  private subscriptions: Subscription[] = [];
  private refreshProviders$: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);
  public energyTypeName: string;
  public energyTypeId: number;
  public scenarioId: number;
  public scenarioName: string;
  public providerId: number;
  public loading = false;
  public sites: { id: number, name: string }[];
  public providerList: KPI.EmissionFactorProvider[] = [];
  public displayRadio: boolean;
  public sitesLabel: string;
  public readOnly: boolean = false;
  @Output() public changed = new EventEmitter<KPI.EmissionFactorProvider>();

  constructor(
    private emissionFactorService: EmissionFactorService,
    private ref: NbDialogRef<EmissionFactorListDialogComponent>,
    private dialogService: NbDialogService,
    private translate: TranslateService,
    private errorService: ErrorService,
    private toastrService: NbToastrService,
    private users: UsersService,
    private perimetersService: PerimetersService
  ) { }

  public ngOnInit(): void {
    let siteIds = [];
    if (this.sites) {
      this.subscriptions.push(
        (
          this.sites.length === 1
            ? of(this.sites[0].name)
            : this.translate.get('core.utils.perimeters.selected_perimeters', { number: this.sites.length})
        ).subscribe((res: string) => this.sitesLabel = res)
      );
      siteIds = this.sites.map(site => site.id);
    } else {
      siteIds = this.perimetersService.retriveSavedPerimeters()[0];
    }
    this.subscriptions.push(
      this.users.currentUserHasReadOnlyForSomePerimeters(siteIds).subscribe(
        readOnlyPermissions => {
          this.readOnly = readOnlyPermissions?.length > 0;
        }
      )
    )

    this.subscriptions.push(
      this.refreshProviders$.pipe(
        switchMap(() =>
          this.emissionFactorService.getEmissionFactorsList(
            this.energyTypeId
          ),
        ),
      ).subscribe((emissionProviders: KPI.EmissionFactorProvider[]) => {
        this.providerList = emissionProviders;
      }),
    );
  }

  public chooseProvider(provider: KPI.EmissionFactorProvider): void {
    const params: KPI.EmissionProviderPayload = {
      sites: this.sites.map((site) => site.id),
      scenario: this.scenarioId,
      provider: provider.id
    };
    this.subscriptions.push(
      this.emissionFactorService.updateProvider(this.energyTypeId, params).subscribe(
        () => {
          this.changed.emit(provider);
          this.ref.close();
        }
      )
    );
  }

  public validate(): void {
    if (this.displayRadio) {
      this.chooseProvider(
        this.providerList.find((provider) => provider.id === this.providerId)
      );
    } else {
      this.ref.close();
    }
  }

  public openProvider(providerId: number): void {
    this.dialogService.open(
      EmissionFactorDialogComponent,
      {
        autoFocus: false,
        context: {
          providerId: providerId,
          energyId: this.energyTypeId,
          energyTypeName: this.energyTypeName,
        }
      }
    );
  }

  public onFileChange(event: any): void {
    this.loading = true;
    /* wire up file reader */
    const target: DataTransfer = <DataTransfer>event.target;
    if (target.files.length !== 1) {
      const error = this.translate.instant('pages.import.import.error.files_length');
      return this.errorService.handleError(error, true);
    }
    const file: File = target.files[0];
    this.subscriptions.push(
      this.emissionFactorService.createProviderFromExcelFile(file).subscribe(
        () => {
          this.loading = false,
          this.refreshProviders$.next()
        },
        (error) => {
          this.errorService.handleError(error, true);
        }
      ),
    );
  }


  public close(): void {
    this.ref.close();
  }

  public getBasedLabel(based: string): string {
    return based === 'MARKET' ? 'Market-based' : 'Location based';
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  public openDeleteConfirmationDialog(providerId: number, providerName: string): void {
    if (this.readOnly) {
      return;
    }

    this.loading = true;
    const name = providerName.charAt(0).toLowerCase() + providerName.slice(1);
    const label = this.translate.instant('Do you want to delete the {provider} ?', { provider: name });

    const confirmDialog = this.dialogService.open(
      ConfirmationDialogComponent,
      {
        context: { label: label },
        autoFocus: false,
      },
    );

    confirmDialog.componentRef.instance.isConfirm.subscribe(() => {
      this.emissionFactorService.deleteEmissionFactor(providerId).subscribe(
        () => {
          this.loading = false,
          this.refreshProviders$.next();
          this.emissionFactorService.emissionFactorChanged();
        },
        (error) => {
          this.loading = false,
          this.toastrService.danger('', error)
        }
      )
    });
  }
}
